import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DeleteIcon from '@mui/icons-material/Delete'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { Box, Button, Divider, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'

import { toast } from '../../../components/LsToast'
import { ReprintDialog } from '../../../components/ReprintDialog'
import { CURRENCY } from '../../../constants/currency'
import { usePosId } from '../../../hooks/usePosId'
import { usePrinter } from '../../../hooks/usePrinter'
import { printReceipt } from '../../../service/print.service'
import { createReceipt, getReceipt } from '../../../service/receipts.service'
import { BookingDto, LessonDto, PaymentType, ReceiptDto } from '../../../service/types'
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage'

export const CART_DRAWER_WIDTH = { sm: 350, xl: 420 }

type LessonGroup = {
  lessonId: string
  productName: string
  date: string
  startTime: string
  endTime: string
  bookings: BookingDto[]
  subtotal: number
}

type Props = {
  customerId: string
  items: BookingDto[]
  lessons: LessonDto[]
  onRemoveLesson: (lessonId: string) => void
  onPaid: () => void
}

export const CustomerCart: FC<Props> = ({ customerId, items, lessons, onRemoveLesson, onPaid }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [submitting, setSubmitting] = useState(false)
  const { posId } = usePosId()
  const { printerIp } = usePrinter()
  const [reprintOpen, setReprintOpen] = useState(false)
  const [reprintReceipt, setReprintReceipt] = useState<ReceiptDto | null>(null)
  const [retrying, setRetrying] = useState(false)

  const lessonMap = useMemo(() => new Map(lessons.map((l) => [l.id, l])), [lessons])

  const lessonGroups = useMemo(() => {
    const map = new Map<string, LessonGroup>()
    for (const b of items) {
      const existing = map.get(b.lessonId)
      if (existing) {
        existing.bookings.push(b)
        existing.subtotal += Number(b.price)
      } else {
        const lesson = lessonMap.get(b.lessonId)
        map.set(b.lessonId, {
          lessonId: b.lessonId,
          productName: lesson?.product?.name ?? '',
          date: lesson?.date ?? '',
          startTime: lesson?.startTime ?? '',
          endTime: lesson?.endTime ?? '',
          bookings: [b],
          subtotal: Number(b.price),
        })
      }
    }
    return [...map.values()]
  }, [items, lessonMap])

  const totalBase = useMemo(() => items.reduce((sum, b) => sum + Number(b.basePrice), 0), [items])
  const totalVat = useMemo(() => items.reduce((sum, b) => sum + Number(b.vat), 0), [items])
  const totalPrice = useMemo(() => items.reduce((sum, b) => sum + Number(b.price), 0), [items])

  const handlePay = async (paymentType: PaymentType) => {
    if (items.length === 0) return
    setSubmitting(true)
    try {
      const receipt = await createReceipt({
        customerId,
        bookingIds: items.map((b) => b.id),
        paymentType,
        posId,
      })
      toast.success(t('general.notification.savedSuccessfully'))
      onPaid()
      if (printerIp && receipt.data) {
        try {
          const fullReceipt = await getReceipt(receipt.data.id)
          await printReceipt(printerIp, fullReceipt.data, t)
          toast.success(t('print.success'))
        } catch {
          setReprintReceipt(receipt.data)
          setReprintOpen(true)
        }
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRetryPrint = async () => {
    if (!reprintReceipt || !printerIp) return
    setRetrying(true)
    try {
      const fullReceipt = await getReceipt(reprintReceipt.id)
      await printReceipt(printerIp, fullReceipt.data, t)
      toast.success(t('print.success'))
      setReprintOpen(false)
      setReprintReceipt(null)
    } catch {
      toast.error(t('print.failed'))
    } finally {
      setRetrying(false)
    }
  }

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <ShoppingCartIcon fontSize="small" />
        <Typography variant="h6">{t('customerDetail.cart.title')}</Typography>
      </Box>

      {/* Items grouped by lesson */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {items.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            {t('customerDetail.cart.empty')}
          </Typography>
        ) : (
          <Box>
            {lessonGroups.map((group) => (
              <Box key={group.lessonId} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} noWrap>
                      {group.productName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.date} {group.startTime}–{group.endTime}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => onRemoveLesson(group.lessonId)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                {group.bookings.map((b) => (
                  <Box key={b.id} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {b.skierName} ({b.skierAge})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Number(b.price).toFixed(2)} {CURRENCY}
                    </Typography>
                  </Box>
                ))}
                {group.bookings.length > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {group.subtotal.toFixed(2)} {CURRENCY}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Summary + Pay buttons */}
      {items.length > 0 && (
        <Box sx={{ px: 2, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{t('customerDetail.cart.netAmount')}</Typography>
            <Typography variant="body2">{totalBase.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{t('customerDetail.cart.vat')}</Typography>
            <Typography variant="body2">{totalVat.toFixed(2)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {t('customerDetail.cart.total')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {totalPrice.toFixed(2)} {CURRENCY}
            </Typography>
          </Box>
          {(!posId || !printerIp) && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {!posId ? t('checkout.posIdRequired') : t('print.noPrinter')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handlePay(PaymentType.Cash)}
              disabled={submitting || !posId || !printerIp}
              fullWidth
            >
              {t('customerDetail.cart.payWithCash')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handlePay(PaymentType.Card)}
              disabled={submitting || !posId || !printerIp}
              fullWidth
            >
              {t('customerDetail.cart.payWithCard')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )

  const open = items.length > 0

  const reprintDialog = (
    <ReprintDialog
      open={reprintOpen}
      onCancel={() => {
        setReprintOpen(false)
        setReprintReceipt(null)
      }}
      onRetry={handleRetryPrint}
      retrying={retrying}
    />
  )

  if (isSmallScreen) {
    return (
      <>
        {reprintDialog}
        <Drawer anchor="right" open={open} PaperProps={{ sx: { width: '100%' } }}>
          {drawerContent}
        </Drawer>
      </>
    )
  }

  return (
    <>
      {reprintDialog}
      <Drawer
        variant="persistent"
        anchor="right"
        open={open}
        sx={{
          width: CART_DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: CART_DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  )
}
